import { useState } from 'react';
import type { LegacyImageProps as ImageProps } from '@dx-ui/osc-base-image';
import { BaseImage as Image } from '@dx-ui/osc-base-image';
import { CarouselNoImage } from './carousel-no-image';
import { Caption } from '@dx-ui/osc-caption';
import type { CaptionProps } from '@dx-ui/osc-caption';
import cx from 'classnames';
import { ANALYTICS_GLOBAL_CLICK_EVENT, trackEvent } from '@dx-ui/config-metrics';
import type { CarouselSingleMetrics } from './types/carousel-analytics';
import { SkeletonLoader } from '@dx-ui/osc-skeleton-loader';

export type CarouselImageProps = {
  /** Image alt text. Defaults to "Image showcasing selected Hotel" */
  alt?: string | null;
  /** Pass in overrides for next/image component */
  imageProps?: Omit<ImageProps, 'src'>;
  /** source of image */
  url: string;
  captionData?: CaptionProps;
  /** Whether it is the currently active image */
  isActive?: boolean;
  /** Analytics data */
  metrics?: CarouselSingleMetrics;
  /** Caption link click handler. When provided, it owns tracking for this interaction. */
  onCaptionClick?: () => void;
};

/**
 * Component to render the carousel image. Includes error handling that will return a "no photo available" placeholder
 * in case of image error.
 * @returns JSX.Element
 */
export const CarouselImage = ({
  alt,
  imageProps,
  url,
  captionData,
  isActive,
  metrics,
  onCaptionClick,
}: CarouselImageProps) => {
  const [isImageError, setIsImageError] = useState(() => !url);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  return (
    <>
      {!isImageError && !isImageLoaded ? (
        <SkeletonLoader className="image-corner-radius absolute inset-0 size-full shrink-0" />
      ) : null}
      {isImageError ? (
        <CarouselNoImage isFullSize />
      ) : (
        <figure className="relative size-full">
          <Image
            isLegacyMode
            alt={alt || ''}
            className={cx(
              'image-corner-radius absolute inset-0 size-full object-cover duration-500',
              {
                'opacity-0': !isImageLoaded,
                'opacity-100': isImageLoaded,
              }
            )}
            layout="fill"
            onError={() => {
              setIsImageError(true);
            }}
            onLoad={() => {
              setIsImageLoaded(true);
            }}
            src={url}
            unoptimized={true}
            data-testid="carousel-image"
            aria-hidden={isActive ? 'false' : 'true'}
            {...imageProps}
          />
          {captionData && isActive ? (
            <Caption
              caption={captionData?.caption}
              captionLink={captionData?.captionLink}
              metricsOnClick={() => {
                if (onCaptionClick) {
                  onCaptionClick();
                  return;
                }
                if (metrics) trackEvent(ANALYTICS_GLOBAL_CLICK_EVENT, metrics);
              }}
            />
          ) : null}
        </figure>
      )}
    </>
  );
};
